import { RouteRecordRaw } from "vue-router";
import StartScreen from "@/views/user/login/StartScreen.vue";
import { requiresPermission } from "@/functions/check";
import { PERMISSION_KEY } from "@/types/user";

export enum ROUTE_NAME {
    START = 'StartScreen',
    LOGIN = 'Login',
    PASSWORD_RESET = 'PasswordReset',
    PASSWORD_RESET_REQUEST = 'PasswordResetRequest',
    REGISTER_CHOICE = 'RegisterChoice',
    REGISTER_TEAM = 'RegisterTeam',
    REGISTER_TEAM_SUCCESS = 'RegisterTeamSuccess',
    REGISTER_ADMIN = 'RegisterAdmin',
    REGISTER_ADMIN_SUCCESS = 'RegisterAdminSuccess',
    LOCATION_INTRO = 'LocationIntro',
    LOCATION_CREATE = 'LocationCreate',
    LOCATION_CREATE_CHOICE = 'LocationCreateChoice',
    LOCATION_JOIN = 'LocationJoin',
    LOCATION_DATA = 'LocationData',
    LOCATION_DELIVERER = 'LocationDeliverer',
    LOCATION_CONFIRM = 'LocationConfirm',
    LOCATION_CONTACT = 'LocationContact',
    DASHBOARD_HOME = 'DashboardHome',
    DASHBOARD_HOME_LIST = 'DashboardHomeList',
    DASHBOARD_CHAT = 'DashboardChat',
    DASHBOARD_MESSAGE = 'DashboardMessage',
    DASHBOARD_MESSAGE_LIST = 'DashboardMessageList',
    DASHBOARD_MESSAGE_ORDER_DETAILS = 'DashboardMessageOrderDetails',
    DASHBOARD_LIST = 'DashboardList',
    DASHBOARD_PROMOTION = 'DashboardPromotion',
    DASHBOARD_SETTINGS = 'DashboardSettings',
    SETTING_PROFILE = 'SettingProfile',
    SETTING_PROFILE_CHANGE_PASSWORD = 'ProfileChangePassword',
    SETTING_LOCATION = 'SettingLocation',
    SETTING_ORDER = 'SettingOrder',
    SETTING_ORDER_NOTIFICATION = 'SettingOrderNotification',
    SETTING_TEAM = 'SettingTeam',
    SETTING_TEAM_INVITE = 'SettingTeamInvite',
    SETTING_DELIVERER = 'SettingDeliverer',
    SETTING_DELIVERER_SELECT = 'SettingDelivererSelect',
    SETTING_DELIVERER_PROFILE = 'SettingDelivererProfile',
    SETTING_DELIVERER_CONFIRM = 'SettingDelivererConfirm',
    SETTING_NOTIFICATION = 'SettingNotification',
    SETTING_DISPLAY = 'SettingDisplay',
    SETTING_STATISTIC = 'SettingStatistic',
    SETTING_SUPPORT = 'SettingSupport',
    SETTING_INTRO_SLIDE = 'SettingIntroSlide',
    IMPRINT = 'Imprint',
    ORDER_DETAILS_SETTING = 'OrderDetailsSetting',
    ORDER_DETAILS_HOME = 'OrderDetailsHome',
    ORDER_LIST_LIST = 'ListContentList',
    ORDER_LIST_HOME = 'ListContentHome',
    ORDER_DELIVERY_HOME = 'OrderDeliveryHome',
    ORDER_DELIVERY_LIST = 'OrderDeliveryList',
    ORDER_DELIVERY_PROMOTION = 'OrderDeliveryPromotion',
    ORDER_PAYMENT_HOME = 'OrderPaymentHome',
    ORDER_PAYMENT_LIST = 'OrderPaymentList',
    ORDER_PAYMENT_PROMOTION = 'OrderPaymentPromotion',
    HOME = 'Home',
    NOTIFICATION_DETAILS = 'NotificationDetails',
    ADD_DELIVERER = 'AddDeliverer',
    SETTING_DELIVERER_ADD = 'SettingAddDeliverer',
    EDIT_CUSTOM_SUPPLIER = 'EditCustomSupplier',
    UPDATE_LOADER = 'UpdateLoader'
}

const dashboardHome: RouteRecordRaw[] = [
    {
        path: '/dashboard/:locationId/home',
        name: ROUTE_NAME.DASHBOARD_HOME,
        component: () => import(/* webpackChunkName: "DashboardHome" */ '@/views/user/dashboard/DashboardHome.vue')
    },
    {
        path: '/dashboard/:locationId/home/order-list/:listId',
        name: ROUTE_NAME.ORDER_LIST_HOME,
        meta: {
            root: ROUTE_NAME.DASHBOARD_HOME,
            noTabs: true
        },
        component: () => import(/* webpackChunkName: "ListContent" */ '@/views/user/ListContent.vue')
    },
    {
        path: '/dashboard/:locationId/home/order-details/:orderId',
        name: ROUTE_NAME.ORDER_DETAILS_HOME,
        meta: {
            title: 'Bestellungsdetails',
            root: ROUTE_NAME.DASHBOARD_HOME,
            noTabs: true
        },
        component: () => import(/* */ '@/views/user/settings/OrderDetails.vue')
    },
    {
        path: '/dashboard/:locationId/home/order-delivery', /*/order/:id/delivery*/
        name: ROUTE_NAME.ORDER_DELIVERY_HOME,
        meta: {
            title: 'Bestellung',
            root: ROUTE_NAME.DASHBOARD_HOME,
            next: ROUTE_NAME.ORDER_PAYMENT_HOME,
            noTabs: true
        },
        component: () => import(/* webpackChunkName: "OrderDelivery" */ '@/views/user/order/OrderDelivery.vue')
    },
    {
        path: '/dashboard/:locationId/home/order-payment', /*/order/:id/delivery*/
        name: ROUTE_NAME.ORDER_PAYMENT_HOME,
        meta: {
            title: 'Bestellung',
            root: ROUTE_NAME.DASHBOARD_HOME,
            prev: ROUTE_NAME.ORDER_DELIVERY_HOME,
            noTabs: true
        },
        component: () => import(/* webpackChunkName: "OrderPayment" */ '@/views/user/order/OrderPayment.vue')
    },
    {
        path: '/dashboard/:locationId/home/:listId/order-list',
        name: ROUTE_NAME.DASHBOARD_HOME_LIST,
        meta: {
            root: ROUTE_NAME.DASHBOARD_HOME,
            noTabs: true
        },
        component: () => import(/* webpackChunkName: "ListContent" */ '@/views/user/ListContent.vue')
    },
]

const dashboardNotification: RouteRecordRaw[] = [
    {
        path: '/dashboard/:locationId/message-overview',
        name: ROUTE_NAME.DASHBOARD_MESSAGE,
        component: () => import(/* webpackChunkName: "DashboardMessageOverview" */ '@/views/user/dashboard/DashboardMessageOverview.vue'),
    },
    {
        path: '/dashboard/:locationId/message-overview/:listId/order-list',
        name: ROUTE_NAME.DASHBOARD_MESSAGE_LIST,
        meta: {
            root: ROUTE_NAME.DASHBOARD_MESSAGE,
            noTabs: true
        },
        component: () => import(/* webpackChunkName: "ListContent" */ '@/views/user/ListContent.vue')
    },
    {
        path: '/dashboard/:locationId/message-overview/:orderId/order-details',
        name: ROUTE_NAME.DASHBOARD_MESSAGE_ORDER_DETAILS,
        meta: {
            title: 'Bestellungsdetails',
            root: ROUTE_NAME.DASHBOARD_MESSAGE,
            noTabs: true
        },
        component: () => import(/* webpackChunkName: "OrderDetailsMessage" */ '@/views/user/settings/OrderDetails.vue')
    },
]

const dashboardChat: RouteRecordRaw[] = [
    {
        path: '/dashboard/:locationId/chat',
        name: ROUTE_NAME.DASHBOARD_CHAT,
        component: () => import('@/views/user/dashboard/DashboardChat.vue'),
        beforeEnter: () => {
            if (!requiresPermission(PERMISSION_KEY.CHAT_READ)) {
                return { name: ROUTE_NAME.DASHBOARD_HOME }
            }
            return true
        }
    },
];

const dashboardList: RouteRecordRaw[] = [
    {
        path: '/dashboard/:locationId/list-overview',
        name: ROUTE_NAME.DASHBOARD_LIST,
        component: () => import(/* webpackChunkName: "DashboardListOverview" */ '@/views/user/dashboard/DashboardListOverview.vue'),
    },
    {
        path: '/dashboard/:locationId/list-overview/:listId/order-list',
        name: ROUTE_NAME.ORDER_LIST_LIST,
        meta: {
            root: ROUTE_NAME.DASHBOARD_LIST,
            noTabs: true
        },
        component: () => import(/* webpackChunkName: "ListContent" */ '@/views/user/ListContent.vue')
    },
    {
        path: '/dashboard/:locationId/list-overview/order-delivery', /*/order/:id/delivery*/
        name: ROUTE_NAME.ORDER_DELIVERY_LIST,
        meta: {
            title: 'Bestellung',
            root: ROUTE_NAME.DASHBOARD_LIST,
            next: ROUTE_NAME.ORDER_PAYMENT_LIST,
            noTabs: true
        },
        component: () => import(/* webpackChunkName: "OrderDelivery" */ '@/views/user/order/OrderDelivery.vue')
    },
    {
        path: '/dashboard/:locationId/list-overview/order-payment', /*/order/:id/delivery*/
        name: ROUTE_NAME.ORDER_PAYMENT_LIST,
        meta: {
            title: 'Bestellung',
            root: ROUTE_NAME.DASHBOARD_LIST,
            prev: ROUTE_NAME.ORDER_DELIVERY_LIST,
            noTabs: true
        },
        component: () => import(/* webpackChunkName: "OrderPayment" */ '@/views/user/order/OrderPayment.vue')
    }
]

const dashboardPromotion: RouteRecordRaw[] = [
    {
        path: '/dashboard/:locationId/promotion',
        name: ROUTE_NAME.DASHBOARD_PROMOTION,
        component: () => import(/* webpackChunkName: "DashboardPromotionOverview" */ '@/views/user/dashboard/DashboardPromotionOverview.vue')
    },
    {
        path: '/dashboard/:locationId/promotion/order-delivery', /*/order/:id/delivery*/
        name: ROUTE_NAME.ORDER_DELIVERY_PROMOTION,
        meta: {
            title: 'Bestellung',
            root: ROUTE_NAME.DASHBOARD_PROMOTION,
            next: ROUTE_NAME.ORDER_PAYMENT_PROMOTION,
            noTabs: true
        },
        component: () => import(/* webpackChunkName: "OrderDelivery" */ '@/views/user/order/OrderDelivery.vue')
    },
    {
        path: '/dashboard/:locationId/promotion/order-payment', /*/order/:id/delivery*/
        name: ROUTE_NAME.ORDER_PAYMENT_PROMOTION,
        meta: {
            title: 'Bestellung',
            root: ROUTE_NAME.DASHBOARD_PROMOTION,
            prev: ROUTE_NAME.ORDER_DELIVERY_PROMOTION,
            noTabs: true
        },
        component: () => import(/* webpackChunkName: "OrderPayment" */ '@/views/user/order/OrderPayment.vue')
    }
]

const dashboardSetting: RouteRecordRaw[] = [
    {
        path: '/dashboard/:locationId/settings',
        name: ROUTE_NAME.DASHBOARD_SETTINGS,
        component: () => import(/* webpackChunkName: "DashboardSettings" */ '@/views/user/dashboard/DashboardSettings.vue')
    },
    {
        path: '/dashboard/:locationId/settings/profile',
        name: ROUTE_NAME.SETTING_PROFILE,
        meta: {
            title: 'Profil',
            root: ROUTE_NAME.DASHBOARD_SETTINGS,
            noTabs: true
        },
        component: () => import(/* webpackChunkName: "SettingProfile" */ '@/views/user/settings/SettingProfile.vue')
    },
    {
        path: '/dashboard/:locationId/settings/profile/changePassword',
        name: ROUTE_NAME.SETTING_PROFILE_CHANGE_PASSWORD,
        meta: {
            root: ROUTE_NAME.DASHBOARD_SETTINGS,
            noTabs: true
        },
        component: () => import(/* webpackChunkName: "ProfileChangePassword" */ '@/views/user/settings/ProfileChangePassword.vue')
    },
    {
        path: '/dashboard/:locationId/settings/location',
        name: ROUTE_NAME.SETTING_LOCATION,
        meta: {
            title: 'Location bearbeiten',
            root: ROUTE_NAME.DASHBOARD_SETTINGS,
            noTabs: true
        },
        component: () => import(/* webpackChunkName: "SettingLocation" */ '@/views/user/settings/SettingLocation.vue')
    },
    {
        path: '/dashboard/:locationId/settings/order',
        name: ROUTE_NAME.SETTING_ORDER,
        meta: {
            title: 'Bestellungen',
            root: ROUTE_NAME.DASHBOARD_SETTINGS,
            noTabs: true
        },
        component: () => import(/* webpackChunkName: "SettingOrder" */ '@/views/user/settings/SettingOrder.vue')
    },
    {
        path: '/dashboard/:locationId/settings/order-details/:orderId',
        name: ROUTE_NAME.ORDER_DETAILS_SETTING,
        meta: {
            title: 'Bestellungsdetails',
            root: ROUTE_NAME.DASHBOARD_SETTINGS,
            noTabs: true
        },
        component: () => import(/* webpackChunkName: "OrderDetails" */ '@/views/user/settings/OrderDetails.vue')
    },
    {
        path: '/dashboard/:locationId/settings/orderNotification',
        name: ROUTE_NAME.SETTING_ORDER_NOTIFICATION,
        meta: {
            title: 'Bestellwecker',
            root: ROUTE_NAME.DASHBOARD_SETTINGS,
            noTabs: true
        },
        component: () => import(/* webpackChunkName: "SettingOrder" */ '@/views/user/settings/SettingOrderNotification.vue')
    },
    {
        path: '/dashboard/:locationId/settings/team',
        name: ROUTE_NAME.SETTING_TEAM,
        meta: {
            title: 'Team',
            root: ROUTE_NAME.DASHBOARD_SETTINGS,
            noTabs: true
        },
        component: () => import(/* webpackChunkName: "SettingTeam" */ '@/views/user/settings/SettingTeam.vue')
    },
    {
        path: '/dashboard/:locationId/settings/team/invite',
        name: ROUTE_NAME.SETTING_TEAM_INVITE,
        meta: {
            title: 'Team einladen',
            root: ROUTE_NAME.DASHBOARD_SETTINGS,
            noTabs: true
        },
        component: () => import(/* webpackChunkName: "SettingTeamInvite" */ '@/views/user/settings/SettingTeamInvite.vue')
    },
    {
        path: '/dashboard/:locationId/settings/deliverer',
        name: ROUTE_NAME.SETTING_DELIVERER,
        meta: {
            title: 'Lieferanten',
            root: ROUTE_NAME.DASHBOARD_SETTINGS,
            noTabs: true
        },
        component: () => import(/* webpackChunkName: "SettingDeliverer" */ '@/views/user/settings/SettingDeliverer.vue')
    },
    {
        path: '/dashboard/:locationId/settings/deliverer/profile/:supplierId',
        name: ROUTE_NAME.SETTING_DELIVERER_PROFILE,
        meta: {
            title: 'Profil',
            root: ROUTE_NAME.DASHBOARD_SETTINGS,
            noTabs: true
        },
        component: () => import(/* webpackChunkName: "SettingDelivererProfile" */ '@/views/user/settings/SettingDelivererProfile.vue')
    },
    {
        path: '/dashboard/:locationId/settings/deliverer/profile/:supplierId/edit',
        name: ROUTE_NAME.EDIT_CUSTOM_SUPPLIER,
        meta: {
            title: 'Lieferant editieren',
            root: ROUTE_NAME.DASHBOARD_SETTINGS,
            noTabs: true
        },
        component: () => import(/* webpackChunkName: "EditCustomSupplier" */ '@/views/user/settings/EditCustomSupplier.vue')
    },
    {
        path: '/dashboard/:locationId/settings/deliverer/select',
        name: ROUTE_NAME.SETTING_DELIVERER_SELECT,
        meta: {
            title: 'Lieferanten wählen',
            root: ROUTE_NAME.DASHBOARD_SETTINGS,
            noTabs: true
        },
        component: () => import(/* webpackChunkName: "SettingDelivererSelect" */ '@/views/user/settings/SettingDelivererSelect.vue')
    },
    {
        path: '/dashboard/:locationId/settings/deliverer/add',
        name: ROUTE_NAME.SETTING_DELIVERER_ADD,
        meta: {
            root: ROUTE_NAME.DASHBOARD_SETTINGS,
            noTabs: true
        },
        component: () => import(/* webpackChunkName: "LocationAddDelivererSetting" */ '@/views/user/settings/SettingLocationAddDeliverer.vue')
    },
    {
        path: '/dashboard/:locationId/settings/deliverer/confirm',
        name: ROUTE_NAME.SETTING_DELIVERER_CONFIRM,
        meta: {
            root: ROUTE_NAME.DASHBOARD_SETTINGS,
            noTabs: true
        },
        component: () => import(/* webpackChunkName: "SettingDelivererConfirm" */ '@/views/user/settings/SettingDelivererConfirm.vue')
    },
    {
        path: '/dashboard/:locationId/settings/notification',
        name: ROUTE_NAME.SETTING_NOTIFICATION,
        meta: {
            title: '',
            root: ROUTE_NAME.DASHBOARD_SETTINGS,
            noTabs: true
        },
        component: () => import(/* webpackChunkName: "SettingNotification" */ '@/views/user/settings/SettingNotification.vue')
    },
    {
        path: '/dashboard/:locationId/settings/notification/details',
        name: ROUTE_NAME.NOTIFICATION_DETAILS,
        meta: {
            root: ROUTE_NAME.SETTING_NOTIFICATION,
            noTabs: true
        },
        component: () => import(/* webpackChunkName: "NotificationDetails" */ '@/views/user/settings/NotificationDetails.vue')
    },
    {
        path: '/dashboard/:locationId/settings/display',
        name: ROUTE_NAME.SETTING_DISPLAY,
        meta: {
            title: 'Darstellungsoptionen',
            root: ROUTE_NAME.DASHBOARD_SETTINGS,
            noTabs: true
        },
        component: () => import(/* webpackChunkName: "SettingDisplay" */ '@/views/user/settings/SettingDisplay.vue')
    },
    {
        path: '/dashboard/:locationId/settings/statistic',
        name: ROUTE_NAME.SETTING_STATISTIC,
        meta: {
            title: '',
            root: ROUTE_NAME.DASHBOARD_SETTINGS,
            noTabs: true
        },
        component: () => import(/* webpackChunkName: "SettingStatistic" */ '@/views/user/settings/SettingStatistic.vue')
    },
    {
        path: '/dashboard/:locationId/settings/imprint',
        name: ROUTE_NAME.IMPRINT,
        meta: {
            title: '',
            root: ROUTE_NAME.DASHBOARD_SETTINGS,
            noTabs: true
        },
        component: () => import(/* webpackChunkName: "Imprint" */ '@/views/user/settings/SettingImprint.vue')
    },
    {
        path: '/dashboard/:locationId/settings/support',
        name: ROUTE_NAME.SETTING_SUPPORT,
        meta: {
            title: '',
            root: ROUTE_NAME.DASHBOARD_SETTINGS,
            noTabs: true
        },
        component: () => import(/* webpackChunkName: "SettingSupport" */ '@/views/user/settings/SettingSupport.vue')
    },
    {
        path: '/dashboard/:locationId/settings/intro',
        name: ROUTE_NAME.SETTING_INTRO_SLIDE,
        meta: {
            title: '',
            root: ROUTE_NAME.DASHBOARD_SETTINGS,
            noTabs: true
        },
        component: () => import(/* webpackChunkName: "SettingIntro" */ '@/views/user/settings/SettingIntroSlides.vue')
    }
]

export const routes: Array<RouteRecordRaw> = [
    {
        path: '/',
        meta: {
            noAuth: true
        },
        redirect: '/start'
    },
    {
        path: '/update',
        name: ROUTE_NAME.UPDATE_LOADER,
        meta: {
            force: true,
            noTabs: true
        },
        component: () => import(/* webpackChunkName: "UpdateLoader" */ '@/views/user/UpdateLoader.vue')
    },
    {
        path: '/start',
        name: ROUTE_NAME.START,
        meta: {
            noAuth: true,
            noTabs: true
        },
        component: StartScreen
    },
    {
        path: '/login',
        name: ROUTE_NAME.LOGIN,
        meta: {
            noAuth: true,
            noTabs: true
        },
        component: () => import(/**/ '@/views/user/login/Login.vue')
    },
    {
        path: '/reset',
        name: ROUTE_NAME.PASSWORD_RESET,
        meta: {
            disableBack: true,
            noAuth: true,
            noTabs: true
        },
        component: () => import(/* webpackChunkName: "PasswordReset" */ '@/views/user/login/PasswordReset.vue')
    },
    {
        path: '/reset-request',
        name: ROUTE_NAME.PASSWORD_RESET_REQUEST,
        meta: {
            noAuth: true,
            noTabs: true
        },
        component: () => import(/* webpackChunkName: "PasswordResetRequest" */ '@/views/user/login/PasswordResetRequest.vue')
    },
    {
        path: '/register-choice',
        name: ROUTE_NAME.REGISTER_CHOICE,
        meta: {
            noAuth: true,
            noTabs: true
        },
        component: () => import(/* webpackChunkName: "RegisterChoice" */ '@/views/user/register/RegisterChoice.vue')
    },
    {
        path: '/register-team',
        name: ROUTE_NAME.REGISTER_TEAM,
        meta: {
            noAuth: true,
            noTabs: true
        },
        component: () => import(/* webpackChunkName: "RegisterTeam" */ '@/views/user/register/RegisterTeam.vue')
    },
    {
        path: '/register-team-success',
        name: ROUTE_NAME.REGISTER_TEAM_SUCCESS,
        meta: {
            disableBack: true,
            noTabs: true
        },
        component: () => import(/* webpackChunkName: "RegisterTeamSuccess" */ '@/views/user/register/RegisterTeamSuccess.vue')
    },
    {
        path: '/register-admin',
        name: ROUTE_NAME.REGISTER_ADMIN,
        meta: {
            noAuth: true,
            noTabs: true
        },
        component: () => import(/* webpackChunkName: "RegisterAdmin" */ '@/views/user/register/RegisterAdmin.vue')
    },
    {
        path: '/register-admin-success',
        name: ROUTE_NAME.REGISTER_ADMIN_SUCCESS,
        meta: {
            disableBack: true,
            noTabs: true
        },
        component: () => import(/* webpackChunkName: "RegisterAdminSuccess" */ '@/views/user/register/RegisterAdminSuccess.vue')
    },
    {
        path: '/location-intro',
        name: ROUTE_NAME.LOCATION_INTRO,
        meta: {
            disableBack: true,
            noTabs: true
        },
        component: () => import(/* webpackChunkName: "LocationIntro" */ '@/views/user/location/LocationIntro.vue')
    },
    {
        path: '/location-create',
        name: ROUTE_NAME.LOCATION_CREATE,
        meta: {
            title: 'Location erstellen',
            noTabs: true
        },
        component: () => import(/* webpackChunkName: "LocationCreate" */ '@/views/user/location/LocationCreate.vue')
    },
    {
        path: '/location-join',
        name: ROUTE_NAME.LOCATION_JOIN,
        meta: {
            noTabs: true
        },
        component: () => import(/* webpackChunkName: "LocationJoin" */ '@/views/user/location/LocationJoin.vue')
    },
    {
        path: '/location-data',
        name: ROUTE_NAME.LOCATION_DATA,
        meta: {
            title: 'Location erstellen',
            noTabs: true
        },
        component: () => import(/* webpackChunkName: "LocationData" */ '@/views/user/location/LocationData.vue')
    },
    {
        path: '/:locationId/location-deliverer',
        name: ROUTE_NAME.LOCATION_DELIVERER,
        meta: {
            title: 'Location erstellen',
            disableBack: true,
            noTabs: true
        },
        component: () => import(/* webpackChunkName: "LocationDeliverer" */ '@/views/user/location/LocationDeliverer.vue')
    },
    {
        path: '/:locationId/location-deliverer-add',
        name: ROUTE_NAME.ADD_DELIVERER,
        meta: {
            noTabs: true
        },
        component: () => import(/* webpackChunkName: "AddDeliverer" */ '@/views/user/location/LocationAddDeliverer.vue')
    },
    {
        path: '/location-confirm',
        name: ROUTE_NAME.LOCATION_CONFIRM,
        meta: {
            noTabs: true
        },
        component: () => import(/* webpackChunkName: "LocationConfirm" */ '@/views/user/location/LocationConfirm.vue')
    },
    {
        path: '/location-contact',
        name: ROUTE_NAME.LOCATION_CONTACT,
        meta: {
            noTabs: true
        },
        component: () => import(/* webpackChunkName: "LocationContact" */ '@/views/user/location/LocationContact.vue')
    },
    {
        path: '/home',
        name: ROUTE_NAME.HOME,
        meta: {
            disableBack: true,
            noTabs: true
        },
        component: () => import(/* webpackChunkName: "Home" */ '@/views/user/Home.vue')
    },
    {
        path: '/location-create-choice',
        name: ROUTE_NAME.LOCATION_CREATE_CHOICE,
        meta: {
            noTabs: true
        },
        component: () => import(/* webpackChunkName: "LocationCreateChoice" */ '@/views/user/location/LocationCreateChoice.vue')
    },
    ...dashboardHome,
    ...dashboardNotification,
    ...dashboardChat,
    ...dashboardList,
    ...dashboardPromotion,
    ...dashboardSetting
]
